/**
 * <PageHeader> — canonical page header.
 *
 * Composes <Heading level={1}> with optional description and an actions slot,
 * wrapped in the locked enterprise hero shell (subtle horizontal gradient,
 * rounded card chrome) so every operational page reads the same.
 *
 * Replaces the 8 distinct `*Header*.tsx` components scattered across
 * features (Phase-1 §1.2 / M15) and the ad-hoc `<div>` page-title patterns
 * in pages.
 *
 * The locked treatment from D.2 / Strategist A:
 *   - Title  = <Heading level={1}>  → text-xl font-semibold text-text
 *   - Description = <Text variant="body-sm" tone="muted">
 *   - Actions sit on the right, centered to the title
 *   - Surface = `pageHeaderShellClass` — gradient + rounded card
 *
 * Pages that need a fully bespoke header layout (icon-prefixed titles,
 * stats rows, etc.) can wrap their own JSX in `pageHeaderShellClass`
 * directly.
 *
 * Spec: docs/design-system/02-typography.md, 03-spacing-layout.md, 09-navigation-shell.md.
 */

import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../utils/cn';
import { Heading } from './Heading';
import { Text } from './Text';

/**
 * Canonical chrome for any page header — subtle horizontal gradient over the
 * surface, rounded card border, generous interior padding, bottom margin.
 *
 * Apply directly to a `<header>` or `<div>` when you need a custom header
 * layout that <PageHeader> can't express.
 */
export const pageHeaderShellClass =
  'bg-gradient-to-r from-primary/5 via-transparent to-primary/5 rounded-lg border border-border px-6 py-6 mb-3';

export interface PageHeaderProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /** Page title — rendered as <Heading level={1}>. */
  title: ReactNode;
  /** Sub-line under the title. Optional. */
  description?: ReactNode;
  /** Right-aligned actions (buttons, links, view selectors). */
  actions?: ReactNode;
  /** Eyebrow label above the title. Rare — use sparingly. */
  eyebrow?: ReactNode;
  /** Breadcrumbs row above the title. */
  breadcrumb?: ReactNode;
  /** Drop the carded gradient shell — render the title row inline only. */
  shell?: false;
  /**
   * Highlight the final word of a string title in `text-primary` (the canonical
   * "Audit Reports" pattern). Default on; conservative — only multi-word string
   * titles are affected (JSX/dynamic titles render unchanged).
   */
  accent?: boolean;
}

export const PageHeader = forwardRef<HTMLElement, PageHeaderProps>(function PageHeader(
  { title, description, actions, eyebrow, breadcrumb, shell, accent = true, className, ...rest },
  ref,
) {
  const shellClass = shell === false ? '' : pageHeaderShellClass;
  return (
    <header ref={ref} className={cn(shellClass, 'flex flex-col gap-2', className)} {...rest}>
      {breadcrumb && <div className="text-xs text-muted">{breadcrumb}</div>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          {eyebrow && (
            <Text variant="eyebrow" tone="muted">
              {eyebrow}
            </Text>
          )}
          <Heading level={1} accent={accent}>{title}</Heading>
          {description && (
            <Text variant="body-sm" tone="muted">
              {description}
            </Text>
          )}
        </div>
        {actions && <div className="flex items-center gap-3 flex-shrink-0">{actions}</div>}
      </div>
    </header>
  );
});

PageHeader.displayName = 'PageHeader';

export default PageHeader;
